/**
 * Module: screens/creator/_labels
 * Role: Russian display labels for creator profile enums (FTR-1131 amendment
 *       2026-06-07 round 3). 1:1 mapping to backend `models_creator.py`
 *       StrEnum values — confirmed by `grep "class (ContentCategory|UGCFormat|
 *       AudienceGender|AudienceAgeRange)" src/domains/buyer/models_creator.py`.
 *
 *       Round-2 bug founder caught: half the category chips rendered the raw
 *       enum value because `_labels.ts` had stale keys (`fitness`, `tech`,
 *       `humor`) that don't exist in the backend, while real backend keys
 *       (`gadgets`, `sport`, `pets`, `education`, `other`) were missing.
 *       This file is now the single source of truth for «what label does
 *       this enum render as» and MUST be kept in sync with the Python enum
 *       when new options are added.
 *
 *       Backend (`src/domains/buyer/models_creator.py`) returns StrEnum
 *       values like `"beauty" / "video_review" / "female" / "18_24"`. We
 *       render the Russian label to the user, but persist the underlying
 *       enum into the wizard store / API — backend SSOT is preserved.
 *
 * Used by: steps/CategoriesStep, FormatsStep, DemographicsStep, StatusScreen.
 * Glossary: ai/glossary/buyer.md
 */

/** Returns the Russian label for an enum value. Falls back to the raw value
 *  so a backend-added option still renders (just untranslated, surfacing the
 *  drift as a visible TODO rather than crashing). */
function lookup(map: Record<string, string>, value: string): string {
  return map[value] ?? value;
}

// SYMMETRIC LIMIT — sync with `class ContentCategory(StrEnum)` in
// `src/domains/buyer/models_creator.py`. 12 entries as of 2026-06-07.
const CATEGORY_LABELS: Record<string, string> = {
  beauty: 'Бьюти',
  lifestyle: 'Лайфстайл',
  mom_family: 'Родительство',
  home: 'Дом',
  gadgets: 'Гаджеты',
  sport: 'Спорт',
  food: 'Еда',
  travel: 'Путешествия',
  fashion: 'Мода',
  pets: 'Питомцы',
  education: 'Образование',
  other: 'Другое',
};

// SYMMETRIC LIMIT — sync with `class UGCFormat(StrEnum)` in
// `src/domains/buyer/models_creator.py`. 5 entries as of 2026-06-07.
const FORMAT_LABELS: Record<string, string> = {
  photo_review: 'Фотообзор',
  video_review: 'Видеообзор',
  reels_shorts: 'Reels / Shorts',
  stories: 'Stories',
  live: 'Эфиры',
};

// SYMMETRIC LIMIT — sync with `class AudienceGender(StrEnum)` in
// `src/domains/buyer/models_creator.py`. 3 entries as of 2026-06-07.
const GENDER_LABELS: Record<string, string> = {
  female: 'Женский',
  male: 'Мужской',
  mixed: 'Смешанный',
};

// SYMMETRIC LIMIT — sync with `class AudienceAgeRange(StrEnum)` in
// `src/domains/buyer/models_creator.py`. 5 entries as of 2026-06-07.
const AGE_RANGE_LABELS: Record<string, string> = {
  under_18: 'до 18',
  '18_24': '18–24',
  '25_34': '25–34',
  '35_44': '35–44',
  '45_plus': '45+',
};

export const categoryLabel = (value: string): string => lookup(CATEGORY_LABELS, value);
export const formatLabel = (value: string): string => lookup(FORMAT_LABELS, value);
export const genderLabel = (value: string): string => lookup(GENDER_LABELS, value);
export const ageRangeLabel = (value: string): string => lookup(AGE_RANGE_LABELS, value);

// SYMMETRIC — ключи sync с `creator.rejection_reasons` в
// src/domains/buyer/locales/ru.yaml:557-570 (канон текстов, на «ты») +
// whitelist creator_moderation_handler._REJECT_REASONS. 9 кодов as of 2026-08-21
// (TECH-1449 снял код «селфи не совпадает с фото профиля» — такой проверки не существует).
// Тексты — дословная копия ru.yaml (НЕ перефразировать, НЕ менять на «вы»).
const REJECTION_REASON_LABELS: Record<string, string> = {
  low_followers:
    'Слишком мало подписчиков. Чтобы стать креатором, нужно минимум 100 подписчиков в VK или TikTok.',
  fake_profile:
    'Профиль выглядит подозрительно. Если это ошибка, обратись в поддержку.',
  not_real_screenshot:
    'Скрин профиля не похож на настоящий. Пришли скриншот напрямую из приложения VK/TikTok.',
  min_followers_or_fake:
    'Недостаточно подписчиков в публичных профилях VK/TikTok (нужно минимум 100). Если это ошибка — обратись в поддержку @awardy_support.',
  not_real_profile:
    'Похоже, что профиль не настоящий или скриншот сгенерирован. Попробуй ещё раз с реальным скриншотом из приложения.',
  selfie_no_face:
    'На фото не видно лица. Попробуй другое селфи при хорошем освещении.',
  selfie_spoofed:
    'Похоже, это фото фотографии. Сделай оригинальное селфи камерой телефона.',
  selfie_synthetic:
    'Похоже, фото сгенерировано. Сделай настоящее селфи камерой.',
  scraping_failed_after_retries:
    'Не получилось проверить твой профиль автоматически. Попробуй ещё раз через несколько минут.',
};

/** Null-safe label for a rejection-reason code. Returns '' for null/empty
 *  (StatusScreen already wraps the render in a `&&` truthiness guard, so an
 *  empty string keeps the card hidden). Unknown codes fall back to the raw
 *  string — backend-added reason still renders (untranslated, visible TODO). */
export const rejectionReasonLabel = (code: string | null | undefined): string =>
  code ? lookup(REJECTION_REASON_LABELS, code) : '';
